import { useState, useEffect } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import useMenuBack from '../../hooks/useMenuBack'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Globe, Bell, Mail, MessageSquare, Lock, Shield, Download, ChevronRight, Check, Trash2, Settings, KeyRound, Sparkles, AlertTriangle, LifeBuoy, ScanFace, Smartphone } from 'lucide-react'
// s114 Face ID opt-in (Dr. Rami 2026-10-22, canvas F1 approved)
import { biometricAvailable, verifyBiometric, biometricDiagnose } from '../../utils/biometric'
import { getFaceIdPref, saveFaceIdPref } from '../../utils/faceIdPref'
// REP-10 PA-3: Help & Support → "Report a problem", behind REPORT_PROBLEM_ENABLED (OFF).
import { REPORT_PROBLEM_ENABLED } from '../../config/featureFlags'
import ReportProblemModal from '../../components/support/ReportProblemModal'
import { lastReportRoutePattern } from '../../utils/reportRouteMemory'
import { useProfileStore, useUserStore } from '../../stores'
import { notificationsAPI, dataExportAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import LanguageSelector from '../../components/LanguageSelector'
import HIPAADisclaimerModal from '../../components/HIPAADisclaimerModal'
import { generateRecordsPackage } from '../../utils/recordsPackage'
import { saveOrShareBlob } from '../../utils/saveOrShareBlob'
// P5 D4 (PROD-171): the patient builds her own export and downloads it through a 600 s link.
import {
  runMyDataExport, redownloadMyDataExport, deliverExportLink, openLinkOnDevice, exportFileName,
} from '../../utils/dataExport'
import { apiErrorCode } from '../../utils/apiErrorMessage'
import { CONTACT_EMAIL_ROUTE, CONTACT_PHONE_ROUTE, maskedPhone } from '../../utils/contactChange'
import { isSmsOptedOutKnown } from '../../utils/smsRefusal'
import useTwoFactorStatus from '../../hooks/useTwoFactorStatus'
import {
  twoFactorPosture, activeMethod, ssoProvider,
  POSTURE_LOADING, POSTURE_ERROR, POSTURE_SSO, POSTURE_ON,
} from '../../utils/securityPosture'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * SettingsScreen v156.2.0 — Redesigned with data export flow
 * 
 * Matches approved mockup: icon containers, section labels (Preferences,
 * Notifications, Security, Data), inline toggles, clean hierarchy.
 *
 * ★★★ P5 D4 (Alex, 2026-09-26): "The patient starts the export in the app and downloads
 *   the zip through a short-lived link. No admin handles the data." Export my data opens
 *   the export sheet; "Download my complete record" goes through the same HIPAA + PIN gate
 *   as the printable summary, then POST /data-export/generate builds the zip and answers a
 *   600 s link (utils/dataExport.js), and the file is saved to the device. An export the
 *   server could only build in part is shown as incomplete, with the count. The old
 *   "Email me a copy instead" filed a request an admin was to fulfil within 48 hours; that
 *   is the admin handling D4 rules out, so it is replaced by the export itself.
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE = '#E9F8FF'

function SectionLabel({ children, onClick }) {
  return (
    <p
      onClick={onClick}
      className="font-bold uppercase ml-0.5"
      style={{ fontSize: '8.5px', letterSpacing: '0.1em', color: '#94A3B8', margin: '16px 0 6px 2px' }}
    >
      {children}
    </p>
  )
}

function Toggle({ on, onChange, disabled = false, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={!!on}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange() }}
      className="flex-shrink-0 relative"
      style={{
        width: 38, height: 22, borderRadius: 999, border: 'none',
        background: disabled ? '#E2E8F0' : on ? BLUE : '#D1D5DB', cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <div
        className="absolute bg-white rounded-full"
        style={{
          width: 16, height: 16, top: 3,
          left: on ? 19 : 3, transition: 'left 0.15s',
        }}
      />
    </button>
  )
}

function SettingsRow({ icon: Icon, label, subtitle, right, accent, onClick }) {
  const Wrapper = onClick ? 'button' : 'div'
  return (
    <Wrapper
      onClick={onClick}
      className="w-full flex items-center gap-2.5"
      style={{
        padding: '10px 2px', border: 'none', background: 'none',
        cursor: onClick ? 'pointer' : 'default', textAlign: 'left',
      }}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center"
        style={{
          width: 32, height: 32, borderRadius: 9,
          background: accent ? `${BLUE}14` : '#F1F5F9',
        }}
      >
        <Icon size={14} color={accent ? BLUE : '#64748B'} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="block" style={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{label}</span>
        {subtitle && (
          <p className="mt-px" style={{ fontSize: 9, color: accent ? BLUE : '#94A3B8' }}>{subtitle}</p>
        )}
      </div>
      {right}
      {onClick && !right && <ChevronRight size={12} className="flex-shrink-0" style={{ color: '#CBD5E1' }} />}
    </Wrapper>
  )
}

export default function SettingsScreen() {
  const navigate = useNavigate()
  const goBack = useMenuBack()
  const profile = useProfileStore((state) => state.profile)
  const user = useUserStore((state) => state.user)
  const refreshUser = useUserStore((state) => state.refreshUser)
  const { t, hasText, currentLanguage } = useI18n()
  // REP-10 PA-3. Flag off: never true, the row is not drawn. Flag on: drawn only where the UI
  // language has approved text (en today; es is PENDING_ES_WITHHELD until Dr. Rami approves).
  const showReportRow = REPORT_PROBLEM_ENABLED && hasText('support.sectionTitle') && hasText('support.reportRow')
  const [showReport, setShowReport] = useState(false)

  // v238.22.0: notification master toggles are server-backed (profiles.settings
  // via /notification-preferences). Push/email default on; SMS stays off until
  // the server confirms the patient consented (settings.smsNotifications).
  const [settings, setSettings] = useState({
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
  })
  const [savingToggle, setSavingToggle] = useState(false)
  const [smsOptedOut, setSmsOptedOut] = useState(false) // PROD-1166: true only when the API says `smsOptedOut === true`

  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  // P5 D4: the server export's own result. idle | working | ready | incomplete | error, with
  // requestId/files/failed kept so "Download again" can ask for a fresh 600 s link.
  const [exp, setExp] = useState({ state: 'idle' })
  // Which download the HIPAA + PIN gate unlocks: the printable summary or the full export.
  const [hipaaNext, setHipaaNext] = useState('package')

  // v238.24.0: instant client-side records package (HIPAA-gated)
  const [showHipaa, setShowHipaa] = useState(false)
  const [dl, setDl] = useState({ state: 'idle', pct: 0, msg: '', error: '' }) // idle|working|done|error

  const startInstantDownload = () => {
    setShowExportModal(false)
    setHipaaNext('package')
    setShowHipaa(true)
  }

  const startServerExport = () => {
    setShowExportModal(false)
    setHipaaNext('export')
    setShowHipaa(true)
  }

  const deliverZip = (url) => deliverExportLink(url, {
    fetchImpl: (u) => fetch(u),
    save: saveOrShareBlob,
    open: openLinkOnDevice,
    filename: exportFileName(),
  })

  const runServerExport = async () => {
    setExp({ state: 'working' })
    const r = await runMyDataExport({ api: dataExportAPI, deliver: deliverZip })
    setExp(r.kind === 'error' ? { ...r, state: 'error' } : { ...r, state: r.kind })
  }

  const downloadAgain = async () => {
    const prev = exp
    setExp({ ...prev, state: 'working' })
    const r = await redownloadMyDataExport({
      api: dataExportAPI, deliver: deliverZip,
      requestId: prev.requestId, files: prev.files, failed: prev.failed,
    })
    // The first answer's verdict stands; only a failure replaces it.
    setExp(r.kind === 'error' ? { ...r, state: 'error' } : { ...prev, method: r.method })
  }

  const runPackageDownload = async () => {
    setDl({ state: 'working', pct: 0, msg: t('settings.preparing', 'Preparing…'), error: '' })
    try {
      const { blob, filename } = await generateRecordsPackage({
        profile,
        user,
        onProgress: (msg, pct) => setDl((d) => ({ ...d, msg, pct })),
      })
      await saveOrShareBlob(blob, filename)
      setDl({ state: 'done', pct: 100, msg: '', error: '' })
    } catch (e) {
      setDl({ state: 'error', pct: 0, msg: '', error: e?.message || t('settings.somethingWentWrong', 'Something went wrong.') })
    }
  }

  const sessionPinEnabled = !!user?.sessionPinEnabled

  // ★★ s114 Face ID opt-in (Dr. Rami 2026-10-22, canvas F1 approved). The row shows only on a phone with Face ID set up
  //   (biometricAvailable: never on the web); it is off and disabled until the session PIN is on. Turning it ON asks for
  //   Face ID once and saves only when that succeeds; OFF saves at once. Kept on this phone, per user (faceIdPref).
  const [faceIdAvail, setFaceIdAvail] = useState(false)
  const [faceIdOn, setFaceIdOn] = useState(() => getFaceIdPref(user?.id))
  useEffect(() => {
    let alive = true
    biometricAvailable().then((yes) => { if (alive) setFaceIdAvail(!!yes) })
    return () => { alive = false }
  }, [])
  useEffect(() => { setFaceIdOn(getFaceIdPref(user?.id)) }, [user?.id, sessionPinEnabled])
  // s114 developer diagnostic (build 82: no row on his iPhone): five taps on the Security heading show the raw check.
  const [fidTaps, setFidTaps] = useState(0)
  const [fidDiag, setFidDiag] = useState(null)
  const tapSecurityHeading = () => {
    const n = fidTaps + 1
    setFidTaps(n)
    if (n >= 5) { setFidTaps(0); biometricDiagnose().then((d) => setFidDiag(d)) }
  }
  const toggleFaceId = async () => {
    if (!sessionPinEnabled) return
    if (faceIdOn) { saveFaceIdPref(user?.id, false); setFaceIdOn(false); return }
    const ok = await verifyBiometric(t('lockScreen.faceIdReason'))
    if (!ok) return
    saveFaceIdPref(user?.id, true)
    setFaceIdOn(true)
  }

  // ★★★ v242.132.0 — THIS ROW USED TO BE A LITERAL.
  //     `subtitle={t('settingsMisc.enabledTotpSms')}` rendered "Enabled · TOTP +
  //     SMS" for every account, whatever its real state, while the Security screen
  //     one tap away read the server and said "Not set up". Reported from
  //     TestFlight as two screens contradicting each other; it was one screen
  //     reading and one decorating. It now reads the same source, through the same
  //     pure function, so the two cannot disagree by construction.
  const twoFactor = useTwoFactorStatus()
  const twoFactorSubtitle = (() => {
    const posture = twoFactorPosture(twoFactor)
    if (posture === POSTURE_LOADING) return t('security.loadingSettings', { defaultValue: 'Checking…' })
    // ★ Never claim protection we could not confirm, and never claim its absence.
    if (posture === POSTURE_ERROR) return t('settingsMisc.twofactorUnknown', { defaultValue: "Couldn't check" })
    if (posture === POSTURE_SSO) {
      // ★ Two static keys, not one key with a computed defaultValue — a value
      //   assembled at runtime can never reach a locale file (check-i18n-keys).
      return ssoProvider(twoFactor.status) === 'apple'
        ? t('settingsMisc.twofactorByApple', { defaultValue: 'Secured by Apple' })
        : t('settingsMisc.twofactorByGoogle', { defaultValue: 'Secured by Google' })
    }
    if (posture === POSTURE_ON) {
      return activeMethod(twoFactor.status) === 'sms'
        ? t('settingsMisc.twofactorSms', { defaultValue: 'Enabled · Text message' })
        : t('settingsMisc.twofactorTotp', { defaultValue: 'Enabled · Authenticator app' })
    }
    return t('security.notSetUp', { defaultValue: 'Not set up' })
  })()

  // v239.3.0: sessionPinEnabled is not persisted in the hydrated user store, so
  // on a fresh reload the Session PIN row would read "Off" until the user opened
  // the PIN screen (which refreshes). Pull fresh server state on mount so the
  // badge is correct immediately.
  useEffect(() => {
    refreshUser?.()
  }, [refreshUser])

  // Load saved master toggles from the server.
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const res = await notificationsAPI.getPreferences()
        const m = res?.masterToggles || {}
        if (active) {
          setSettings({
            pushNotifications: m.push ?? true,
            emailNotifications: m.email ?? true,
            smsNotifications: m.sms ?? false,
          })
          // PROD-1166: literal true only; an absent key (older API) is unknown and shows nothing.
          setSmsOptedOut(isSmsOptedOutKnown(res))
        }
      } catch (e) {
        // keep all-on defaults on failure
      }
    })()
    return () => { active = false }
  }, [])

  const handleToggle = async (key) => {
    if (savingToggle) return
    const next = { ...settings, [key]: !settings[key] }
    const prev = settings
    setSettings(next) // optimistic
    setSavingToggle(true)
    try {
      await notificationsAPI.updatePreferences({
        masterToggles: {
          push: next.pushNotifications,
          email: next.emailNotifications,
          sms: next.smsNotifications,
        },
      })
    } catch (e) {
      setSettings(prev) // revert on failure
    } finally {
      setSavingToggle(false)
    }
  }

  const handleExportTap = () => {
    setShowExportModal(true)
  }

  // Language display
  const LANG_NAMES = { en: 'English', es: 'Español', zh: '中文', ar: 'العربية', ko: '한국어', hy: 'Հայերեն', fa: 'فارسی', tl: 'Filipino', ru: 'Русский', ja: '日本語', hi: 'हिन्दी' }
  const langCode = currentLanguage?.code || currentLanguage
  const langName = LANG_NAMES[langCode] || currentLanguage?.nativeName || 'English'

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Header */}
      <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-2.5">
        <button
          onClick={() => goBack()}
          className="p-0"
          style={{ background: 'none', border: 'none' }}
        >
          <ArrowLeft size={18} color="#64748B" strokeWidth={2.5} />
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: NAVY, margin: 0 }}>{t('settings.settings')}</h1>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-8 overflow-auto">

        {/* Preferences */}
        <SectionLabel>{t('settings.preferences', 'Preferences')}</SectionLabel>
        <SettingsRow
          icon={Globe}
          label={t('settings.language')}
          subtitle={langName}
          onClick={() => setShowLanguageModal(true)}
        />

        {/* Notifications */}
        <SectionLabel>{t('settings.notifications')}</SectionLabel>
        <SettingsRow
          icon={Bell}
          label={t('settingsMisc.pushNotifications')}
          right={<Toggle on={settings.pushNotifications} onChange={() => handleToggle('pushNotifications')} />}
        />
        <SettingsRow
          icon={Mail}
          label={t('settingsMisc.emailNotifications')}
          right={<Toggle on={settings.emailNotifications} onChange={() => handleToggle('emailNotifications')} />}
        />
        <SettingsRow
          icon={MessageSquare}
          label={t('settingsMisc.smsNotifications')}
          subtitle={smsOptedOut ? t('settingsMisc.smsUnsubscribed') : undefined}
          right={<Toggle on={settings.smsNotifications} onChange={() => handleToggle('smsNotifications')} />}
        />
        <div style={{ padding: '4px 16px 0' }}>
          <button
            onClick={() => navigate('/notification-preferences')}
            className="flex items-center gap-1.5"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <Settings size={12} color="#46A8F9" />
            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#46A8F9' }}>
              {t('notifications.managePreferences')}
            </span>
          </button>
        </div>

        {/* TEST 3.01: Contact details - the ACCOUNT's e-mail and mobile; each changes only after a code to the new value */}
        <SectionLabel>{t('settings.contactHeading')}</SectionLabel>
        <SettingsRow
          icon={Mail}
          label={t('onboarding.welcome.email')}
          subtitle={user?.email || ''}
          accent
          onClick={() => navigate(CONTACT_EMAIL_ROUTE)}
        />
        <SettingsRow
          icon={Smartphone}
          label={t('onboarding.welcome.phone')}
          subtitle={maskedPhone(user?.phone) || t('security.notSetUp')}
          accent
          onClick={() => navigate(CONTACT_PHONE_ROUTE)}
        />

        {/* Security */}
        <SectionLabel onClick={tapSecurityHeading}>{t('settings.securityHeading', 'Security')}</SectionLabel>
        {fidDiag && (
          <pre data-faceid-diag style={{ fontSize: 10, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#F1F5F9', color: '#334155', padding: 8, borderRadius: 8, margin: '0 2px 8px' }}>{JSON.stringify(fidDiag, null, 1)}</pre>
        )}
        <SettingsRow
          icon={Lock}
          label={t('settingsMisc.changePassword')}
          accent
          onClick={() => navigate('/settings/change-password')}
        />
        <SettingsRow
          icon={KeyRound}
          label={t('security.sessionPin', 'Session PIN')}
          subtitle={sessionPinEnabled ? t('security.active', 'Active') : t('security.off', 'Off')}
          accent
          onClick={() => navigate('/settings/session-pin')}
        />
        {faceIdAvail && (
          <SettingsRow
            icon={ScanFace}
            label={t('faceId.rowTitle')}
            subtitle={sessionPinEnabled ? t('faceId.rowDesc') : t('faceId.needsPin')}
            accent={sessionPinEnabled}
            right={<Toggle on={faceIdOn && sessionPinEnabled} disabled={!sessionPinEnabled} label={t('faceId.rowTitle')} onChange={toggleFaceId} />}
          />
        )}
        <SettingsRow
          icon={Shield}
          label={t('settingsMisc.twofactorAuth')}
          subtitle={twoFactorSubtitle}
          accent
          onClick={() => navigate('/settings/security')}
        />
        {faceIdAvail && (
          <p data-faceid-note style={{ fontSize: 9, lineHeight: 1.5, color: '#94A3B8', margin: '2px 2px 0' }}>{t('faceId.note')}</p>
        )}

        <SectionLabel>AI &amp; Data</SectionLabel>
        <SettingsRow
          icon={Sparkles}
          accent
          label={t('settingsMisc.aiDataConsent', { defaultValue: 'AI & Data Consent' })}
          subtitle={t('settingsMisc.aiDataConsentSub', { defaultValue: 'Manage how AI uses your information' })}
          onClick={() => navigate('/settings/ai-consent')}
        />

        {/* Account */}
        <SectionLabel>{t('settings.account', 'Account')}</SectionLabel>
        <SettingsRow
          icon={Download}
          label={t('settingsMisc.exportMyData')}
          subtitle={t('settingsMisc.downloadACopyOfYour')}
          onClick={handleExportTap}
        />
        <SettingsRow
          icon={Trash2}
          label={t('menu.deleteAccount') || 'Delete account'}
          onClick={() => navigate('/settings/delete-account')}
        />

        {/* REP-10 PA-3 — Help & Support. The report is about the last screen BEFORE Settings
            (utils/reportRouteMemory.js), sent as its route template only. */}
        {showReportRow && (
          <>
            <SectionLabel>{t('support.sectionTitle')}</SectionLabel>
            <SettingsRow
              icon={LifeBuoy}
              label={t('support.reportRow')}
              subtitle={t('support.reportRowSubtitle')}
              onClick={() => setShowReport(true)}
            />
          </>
        )}
      </div>

      {showReportRow && showReport && (
        <ReportProblemModal
          origin="settings"
          routePattern={lastReportRoutePattern()}
          onClose={() => setShowReport(false)}
        />
      )}

      {/* Language Selector Modal */}
      <LanguageSelector
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />

      {/* Export Data Modal — P5 D4: the patient's own export */}
      {showExportModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
          <div
            className="bg-white w-full max-w-md rounded-t-3xl overflow-auto bottom-actions [--ba-min:0px]"
            style={{ maxHeight: '85vh' }}
          >
              <div className="px-5 py-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{ width: 40, height: 40, borderRadius: 11, background: `${BLUE}15` }}
                  >
                    <Download size={18} color={BLUE} />
                  </div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{t('dataExport.title', 'Export your record')}</p>
                    <p style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                      {t('dataExport.subtitle', 'A complete copy of your Orella record, for you to keep.')}
                    </p>
                  </div>
                </div>

                <div className="rounded-xl p-3.5 mb-3" style={{ background: 'white', border: '1px solid #F1F5F9' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: NAVY, marginBottom: 8 }}>
                    {t('dataExport.includes', 'The zip file includes:')}
                  </p>
                  {[
                    ['itemProfile', t('dataExport.itemProfile', 'Your profile, identification and insurance')],
                    ['itemCases', t('dataExport.itemCases', 'Your cases, visit notes and referrals')],
                    ['itemVisits', t('dataExport.itemVisits', 'Your appointments and medications')],
                    ['itemFiles', t('dataExport.itemFiles', 'Your uploaded photos, documents and ID images')],
                  ].map(([id, item]) => (
                    <div key={id} className="flex items-center gap-2 mb-1.5">
                      <Check size={12} color={BLUE} strokeWidth={2.5} />
                      <span style={{ fontSize: 11, color: '#475569' }}>{item}</span>
                    </div>
                  ))}
                </div>

                <p style={{ fontSize: 10, color: '#64748B', marginBottom: 12, lineHeight: 1.5 }}>
                  {t('dataExport.linkNote', 'The file is prepared now and saved to this device. Its download link works for 10 minutes and only for you.')}
                </p>

                <button
                  onClick={startServerExport}
                  style={{
                    width: '100%', padding: 12, borderRadius: 12,
                    background: BLUE, color: 'white', border: 'none',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <Download size={14} />
                  {t('dataExport.downloadRecord', 'Download my complete record')}
                </button>
                <button
                  onClick={startInstantDownload}
                  style={{
                    width: '100%', marginTop: 6, padding: 12, borderRadius: 12,
                    background: 'white', color: NAVY, border: '1px solid #E2E8F0',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {t('dataExport.printableSummary', 'Download a printable summary instead')}
                </button>
                <button
                  onClick={() => setShowExportModal(false)}
                  style={{
                    width: '100%', marginTop: 6, padding: 12, borderRadius: 12,
                    background: '#F1F5F9', color: '#64748B', border: 'none',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {t('dataExport.cancel', 'Cancel')}
                </button>
              </div>
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* v238.24.0: HIPAA gate → instant records package download */}
      <HIPAADisclaimerModal
        isOpen={showHipaa}
        onClose={() => setShowHipaa(false)}
        onConfirm={() => (hipaaNext === 'export' ? runServerExport() : runPackageDownload())}
      />

      {/* P5 D4: the server export — working, ready, incomplete or failed */}
      {exp.state !== 'idle' && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-6">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 text-center">
            {exp.state === 'working' && (
              <>
                <OrellaSpinner size={32} className="mx-auto mb-3" />
                <p className="font-semibold text-gray-800 mb-1">{t('dataExport.preparing', 'Preparing your export…')}</p>
                <p className="text-xs text-gray-400 mt-3">{t('dataExport.keepOpen', 'Keep this screen open until the download starts.')}</p>
              </>
            )}
            {(exp.state === 'ready' || exp.state === 'incomplete') && (
              <>
                {exp.state === 'ready' ? (
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                )}
                <p className="font-semibold text-gray-800 mb-1">
                  {exp.state === 'ready'
                    ? t('dataExport.readyTitle', 'Your export is ready')
                    : t('dataExport.incompleteTitle', 'Some files are not in your export')}
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  {exp.method === 'opened'
                    ? t('dataExport.openedBody', 'The download opened. If it did not start, tap Download again within 10 minutes.')
                    : t('dataExport.savedBody', { defaultValue: 'The zip file was saved to this device. Files included: {{files}}.', files: exp.files ?? '—' })}
                </p>
                {exp.state === 'incomplete' && (
                  <p className="text-sm text-amber-700 mb-4">
                    {t('dataExport.incompleteBody', { defaultValue: 'Files that could not be included: {{failed}}. manifest.json in the zip lists them under "missing". You can start a new export later.', failed: exp.failed ?? '—' })}
                  </p>
                )}
                <button
                  onClick={downloadAgain}
                  className="w-full py-3 bg-white border border-gray-200 rounded-full font-semibold text-sm text-gray-700 mb-2 mt-2"
                >
                  {t('dataExport.downloadAgain', 'Download again')}
                </button>
                <button
                  onClick={() => setExp({ state: 'idle' })}
                  className="w-full py-3 bg-orella-500 text-white rounded-full font-semibold text-sm"
                >
                  {t('common.done', 'Done')}
                </button>
              </>
            )}
            {exp.state === 'error' && (
              <>
                <p className="font-semibold text-gray-800 mb-1">{t('dataExport.failedTitle', 'Your export could not be prepared')}</p>
                <p className="text-sm text-gray-500 mb-4">{`${t(exp.key || 'dataExport.failed')}${apiErrorCode(exp)}`}</p>
                <button
                  onClick={() => { setExp({ state: 'idle' }); startServerExport() }}
                  className="w-full py-3 bg-white border border-gray-200 rounded-full font-semibold text-sm text-gray-700 mb-2"
                >
                  {t('dataExport.tryAgain', 'Start a new export')}
                </button>
                <button
                  onClick={() => setExp({ state: 'idle' })}
                  className="w-full py-3 bg-gray-100 rounded-full font-semibold text-sm text-gray-600"
                >
                  {t('common.close', 'Close')}
                </button>
              </>
            )}
          </div>
        </div>
        </OverlayPortal>
      )}

      {dl.state !== 'idle' && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-6">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 text-center">
            {dl.state === 'working' && (
              <>
                <OrellaSpinner size={32} className="mx-auto mb-3" />
                <p className="font-semibold text-gray-800 mb-1">{t('settings.preparingRecords', 'Preparing your records')}</p>
                <p className="text-sm text-gray-500 mb-4">{dl.msg}</p>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-orella-500 rounded-full transition-all" style={{ width: `${dl.pct}%` }} />
                </div>
                <p className="text-xs text-gray-400 mt-3">{t('settings.keepOpenUntilDownload', 'Keep this screen open until the download starts.')}</p>
              </>
            )}
            {dl.state === 'done' && (
              <>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <p className="font-semibold text-gray-800 mb-1">{t('settings.downloadReady', 'Your download is ready')}</p>
                <p className="text-sm text-gray-500 mb-4">{t('settings.checkDownloads', 'Check your downloads for the zip file.')}</p>
                <button
                  onClick={() => setDl({ state: 'idle', pct: 0, msg: '', error: '' })}
                  className="w-full py-3 bg-orella-500 text-white rounded-full font-semibold text-sm"
                >
                  {t('common.done', 'Done')}
                </button>
              </>
            )}
            {dl.state === 'error' && (
              <>
                <p className="font-semibold text-gray-800 mb-1">{t('settings.downloadFailed', "Couldn't prepare your download")}</p>
                <p className="text-sm text-gray-500 mb-4">{dl.error}</p>
                <button
                  onClick={() => { setDl({ state: 'idle', pct: 0, msg: '', error: '' }); startServerExport() }}
                  className="w-full py-3 bg-white border border-gray-200 rounded-full font-semibold text-sm text-gray-700 mb-2"
                >
                  {t('dataExport.downloadRecord', 'Download my complete record')}
                </button>
                <button
                  onClick={() => setDl({ state: 'idle', pct: 0, msg: '', error: '' })}
                  className="w-full py-3 bg-gray-100 rounded-full font-semibold text-sm text-gray-600"
                >
                  {t('common.close', 'Close')}
                </button>
              </>
            )}
          </div>
        </div>
        </OverlayPortal>
      )}
    </div>
  )
}
